import { Link, useNavigate } from '@tanstack/react-router';
import {
  Inbox,
  Users,
  BarChart3,
  FileBarChart,
  MessageSquareQuote,
  Download,
  Globe,
  Webhook,
  Settings,
  ShieldCheck,
  CreditCard,
  LogOut,
  type LucideIcon,
} from 'lucide-react';
import { useAuthStore } from '../store/auth';
import { logout } from '../api/auth';
import { getApiClient } from '../api/factory';
import { useT, type DictKey } from '../i18n';
import { StatusToggle } from './StatusToggle';
import { ThemeToggle } from './ThemeToggle';

interface NavItem {
  to: string;
  icon: LucideIcon;
  labelKey: DictKey;
  adminOnly?: boolean;
}

const NAV: readonly NavItem[] = [
  { to: '/', icon: Inbox, labelKey: 'nav.conversations' },
  { to: '/live-visitors', icon: Users, labelKey: 'nav.liveVisitors' },
  { to: '/analytics', icon: BarChart3, labelKey: 'nav.analytics' },
  { to: '/reports', icon: FileBarChart, labelKey: 'nav.reports', adminOnly: true },
  { to: '/canned-responses', icon: MessageSquareQuote, labelKey: 'nav.cannedResponses' },
  { to: '/exports', icon: Download, labelKey: 'nav.exports' },
  { to: '/sites', icon: Globe, labelKey: 'nav.sites' },
  { to: '/webhooks', icon: Webhook, labelKey: 'nav.webhooks' },
  { to: '/audit', icon: ShieldCheck, labelKey: 'nav.audit', adminOnly: true },
  { to: '/billing', icon: CreditCard, labelKey: 'nav.billing', adminOnly: true },
  { to: '/settings', icon: Settings, labelKey: 'nav.settings' },
] as const;

export function Sidebar() {
  const session = useAuthStore((s) => s.session);
  const clear = useAuthStore((s) => s.clear);
  const navigate = useNavigate();
  const t = useT();

  async function handleLogout() {
    try {
      await logout(getApiClient());
    } catch {
      // ignore — всё равно чистим локальную сессию
    }
    clear();
    await navigate({ to: '/login' });
  }

  return (
    <aside
      aria-label="Sidebar"
      className="flex h-full w-56 shrink-0 flex-col border-r border-slate-200 bg-white"
    >
      <div className="px-5 pt-5 pb-4">
        <Link to="/" className="text-base font-semibold text-slate-900">
          {t('nav.brand')}
        </Link>
      </div>

      {session && (
        <>
          <nav className="flex flex-1 flex-col gap-0.5 px-2">
            {NAV.filter(
              (item) => !item.adminOnly || session.operator.role === 'admin',
            ).map(({ to, icon: Icon, labelKey }) => (
              <Link
                key={to}
                to={to}
                activeOptions={{ exact: to === '/' }}
                className="flex items-center gap-2.5 rounded-md px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                activeProps={{
                  className:
                    'flex items-center gap-2.5 rounded-md px-3 py-1.5 text-sm bg-accent-50 text-accent-700 font-medium',
                }}
              >
                <Icon size={16} className="shrink-0" aria-hidden="true" />
                <span className="truncate">{t(labelKey)}</span>
              </Link>
            ))}
          </nav>

          <div className="border-t border-slate-200 px-3 py-3">
            <div className="mb-2">
              <StatusToggle />
            </div>
            <div
              className="truncate text-xs text-slate-500"
              title={session.operator.email}
            >
              {session.operator.email}
            </div>
            <div className="mt-2 flex items-center justify-between">
              <button
                type="button"
                onClick={handleLogout}
                className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-900"
              >
                <LogOut size={14} aria-hidden="true" />
                {t('nav.logout')}
              </button>
              <ThemeToggle />
            </div>
          </div>
        </>
      )}
    </aside>
  );
}
